import { useEffect, useMemo, useState } from 'react';
import { Sparkles, X, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  ANATOMY_CARDS,
  ANATOMY_CARD_ORDER,
  anatomyLessonKey,
  type AnatomyCardType,
  type AnatomyHotspot as Hotspot,
} from '@/data/cardAnatomy';
import { useLearnProgress } from '@/hooks/useLearnProgress';
import { AnatomyHotspot } from './AnatomyHotspot';

const TYPE_LABELS: Record<AnatomyCardType, string> = {
  character: 'Character',
  action: 'Action',
  item: 'Item',
  location: 'Location',
};

export function CardAnatomyExplorer() {
  const { isComplete, toggle } = useLearnProgress();
  const [type, setType] = useState<AnatomyCardType>('character');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [celebrated, setCelebrated] = useState<Set<AnatomyCardType>>(new Set());

  const card = ANATOMY_CARDS[type];
  const active = useMemo<Hotspot | null>(
    () => (activeId ? card.hotspots.find((h) => h.id === activeId) ?? null : null),
    [activeId, card],
  );

  const { done, total } = useMemo(() => {
    const t = card.hotspots.length;
    const d = card.hotspots.filter((h) => isComplete(anatomyLessonKey(type, h.id))).length;
    return { done: d, total: t };
  }, [card, type, isComplete]);

  // Deep-link support: #learn/anatomy/<hotspotId>
  useEffect(() => {
    const onNav = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.tab === 'learn' && d.subTab === 'anatomy' && d.anchor) {
        // anchor can be a card type, a hotspot id, or "<type>:<hotspot>"
        const raw = String(d.anchor);
        if (raw.includes(':')) {
          const [t, id] = raw.split(':');
          if ((ANATOMY_CARD_ORDER as string[]).includes(t)) setType(t as AnatomyCardType);
          setActiveId(id || null);
        } else if ((ANATOMY_CARD_ORDER as string[]).includes(raw)) {
          setType(raw as AnatomyCardType);
        } else {
          setActiveId(raw);
        }
      }
    };
    window.addEventListener('lorescout:navigate-subtab', onNav);
    return () => window.removeEventListener('lorescout:navigate-subtab', onNav);
  }, []);

  // Celebrate first time a card type hits 100%.
  useEffect(() => {
    if (total > 0 && done === total && !celebrated.has(type)) {
      setCelebrated((prev) => new Set(prev).add(type));
      toast.success(`${TYPE_LABELS[type]} anatomy mastered!`, {
        description: `You explored all ${total} parts of a ${TYPE_LABELS[type].toLowerCase()} card.`,
      });
    }
  }, [done, total, type, celebrated]);

  const handleGotIt = () => {
    if (!active) return;
    const key = anatomyLessonKey(type, active.id);
    if (!isComplete(key)) toggle(key);
    setActiveId(null);
  };

  return (
    <div id="learn-anatomy" className="scroll-mt-24">
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="font-display text-base font-semibold text-foreground">Card Anatomy</h2>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-ink-emerald/15 px-2 py-0.5 text-[10px] font-medium text-ink-emerald font-body">
            <Check className="h-3 w-3" /> {done} / {total}
          </span>
        </div>
        <p className="text-xs text-muted-foreground font-body leading-relaxed">
          Tap each glowing region on the card to learn what it means. Switch tabs to compare card types.
        </p>
      </div>

      {/* Card type tabs */}
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mb-3 overflow-x-auto">
        {ANATOMY_CARD_ORDER.map((t) => (
          <button
            key={t}
            onClick={() => {
              setType(t);
              setActiveId(null);
            }}
            className={`flex-1 min-w-[70px] rounded-lg py-2 text-[11px] font-medium font-body transition-colors ${
              type === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            }`}
          >
            {TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
          <div
            className={`h-full transition-all ${done === total && total > 0 ? 'bg-ink-emerald' : 'bg-primary'}`}
            style={{ width: `${total === 0 ? 0 : Math.round((done / total) * 100)}%` }}
          />
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground font-body">
          {done} of {total} parts explored on this card type
        </p>
      </div>

      {/* Card schematic */}
      <div className="relative mx-auto" style={{ maxWidth: 320 }}>
        <div className="relative aspect-[2/3] rounded-2xl border border-primary/30 bg-gradient-to-br from-card via-secondary to-card overflow-hidden shadow-lg">
          {/* Inkable swirl border */}
          <div className="absolute inset-1 rounded-2xl border-2 border-dashed border-primary/40 pointer-events-none" />
          {/* Cost hexagon mock */}
          <div className="absolute top-3 left-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/30 border border-primary/50 text-xs font-bold text-foreground font-body pointer-events-none">
            {type === 'action' ? '3' : type === 'item' ? '2' : type === 'location' ? '4' : '5'}
          </div>
          {/* Art block */}
          <div className="absolute left-3 right-3 top-14 bottom-[44%] rounded-lg bg-gradient-to-br from-ink-amber/40 via-ink-amethyst/30 to-ink-sapphire/40 pointer-events-none flex items-center justify-center">
            <span className="text-[10px] text-foreground/40 font-body uppercase tracking-wider">Card Art</span>
          </div>
          {/* Name plate */}
          <div className="absolute left-3 right-3 top-[58%] rounded-md bg-card/80 px-2 py-1 pointer-events-none">
            <p className="text-[11px] font-display font-semibold text-foreground leading-tight">{card.title} Name</p>
            <p className="text-[9px] text-muted-foreground font-body">— Version Subtitle</p>
          </div>
          {/* Type bar */}
          <div className="absolute left-3 right-3 top-[70%] rounded-sm bg-secondary/80 px-2 py-0.5 pointer-events-none text-center">
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-body">{card.title}</span>
          </div>
          {/* Ability box */}
          <div className="absolute left-3 right-3 top-[76%] bottom-[12%] rounded-md bg-background/50 border border-border p-1.5 pointer-events-none">
            <p className="text-[8px] text-muted-foreground font-body leading-tight">
              Ability text appears here. Keywords like <strong>Rush</strong> or <strong>Bodyguard</strong> live in this region.
            </p>
          </div>
          {/* Bottom stats row */}
          {(type === 'character' || type === 'location') && (
            <div className="absolute left-2 right-2 bottom-1 flex justify-between items-center px-1 pointer-events-none">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink-ruby/30 border border-ink-ruby/50 text-[10px] font-bold text-foreground">
                {type === 'location' ? '1' : '4'}
              </span>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink-sapphire/30 border border-ink-sapphire/50 text-[10px] font-bold text-foreground">
                {type === 'location' ? '6' : '5'}
              </span>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink-amber/30 border border-ink-amber/50 text-[10px] font-bold text-foreground">
                {type === 'location' ? '1' : '2'}
              </span>
            </div>
          )}

          {/* Hotspots overlay */}
          {card.hotspots.map((h) => (
            <AnatomyHotspot
              key={h.id}
              hotspot={h}
              active={activeId === h.id}
              done={isComplete(anatomyLessonKey(type, h.id))}
              onClick={() => setActiveId(h.id === activeId ? null : h.id)}
            />
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {active && (
        <div className="mt-4 rounded-xl border border-primary/40 bg-card p-4 animate-fade-in">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider text-primary font-body">{TYPE_LABELS[type]} part</p>
              <h3 className="font-display text-base font-semibold text-foreground">{active.label}</h3>
              <p className="mt-2 text-xs text-foreground/90 font-body leading-relaxed">{active.detail}</p>
            </div>
            <button
              type="button"
              onClick={() => setActiveId(null)}
              aria-label="Close detail"
              className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleGotIt}
              className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors"
            >
              <Check className="h-3.5 w-3.5" /> Got it
            </button>
            {(() => {
              const idx = card.hotspots.findIndex((h) => h.id === active.id);
              const next = card.hotspots[idx + 1];
              if (!next) return null;
              return (
                <button
                  type="button"
                  onClick={() => {
                    const key = anatomyLessonKey(type, active.id);
                    if (!isComplete(key)) toggle(key);
                    setActiveId(next.id);
                  }}
                  className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-foreground font-body hover:bg-muted transition-colors"
                >
                  Next <ArrowRight className="h-3.5 w-3.5" />
                </button>
              );
            })()}
          </div>
        </div>
      )}

      {/* Hint legend */}
      {!active && (
        <p className="mt-3 text-center text-[10px] text-muted-foreground font-body">
          Pulsing = unexplored · Green check = explored
        </p>
      )}
    </div>
  );
}
